import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { DataState, LoadingStatus } from '@lib.core/data-state/data-state';
import { ComparisonType } from '@lib.core/charts/comparison-chart';
import { AspectTabularData, verifyTabularRows } from '@lib.core/data/tabular-data';
import { AspectView, sortAspectViewsByAspectType } from '@lib.core/entities/indicator/indicator-view';
import { AspectSummary } from '@lib.core/entities/indicator/indicator-summary';
import { AspectMetadata } from '@lib.core/entities/indicator/aspect-metadata';
import { AspectFilters } from '@lib.core/entities/indicator/indicator-filters';
import { AspectRanking } from '@lib.core/entities/indicator/indicator-ranking';
import { AspectDatapoint } from '@lib.core/entities/indicator/indicator-datapoint';
import { FactMetadata } from '@lib.core/entities/indicator/fact-metadata';
import { AspectContext } from '@lib.core/entities/indicator/indicator-context';
import { SourceMetadata } from '@lib.core/entities/indicator/source-metadata';
import { DimensionMetadata } from '@lib.core/entities/indicator/dimension-metadata';
import { EChartsGeoJSON } from '@lib.core/entities/echarts/echarts-geojson';
import { AspectViewConfigModelDTO, newAspectViewConfigModelFromDTO } from '@lib.resources/indicator/dtos/indicator-view';
import { getHttpParams } from '@lib.resources/utils/url';
import { ResourceRegistry } from '@lib.resources/resource-registry/resource-registry';
import { EnvironmentResource } from '@lib.resources/environment/resource';
import { AspectResource } from '@lib.resources/indicator/resources/indicator-resource';
import { FactMetadataDTO } from '@lib.resources/indicator/dtos/fact-metadata';
import { DimensionMetadataDTO } from '@lib.resources/indicator/dtos/dimensions-metadata';
import { AspectSummaryDTO } from '@lib.resources/indicator/dtos/indicator-summary';
import { AspectMetadataDTO } from '@lib.resources/indicator/dtos/aspect-metadata';
import { AspectContextDTO } from '@lib.resources/indicator/dtos/indicator-context';
import { SourceMetadataDTO } from '@lib.resources/indicator/dtos/source-metadata';
import { AspectRankingDTO } from '@lib.resources/indicator/dtos/indicator-ranking';
import { AspectDataAvailabilityDTO, AspectDatapointDTO } from '@lib.resources/indicator/dtos/indicator-data';
import { EChartsGeoJSONDTO } from '@lib.resources/echarts/dtos/echarts-geojson';

@Injectable({
  providedIn: 'root',
})
export class AspectApiService implements AspectResource {
  private readonly http = inject(HttpClient);

  private resourceRegistry = inject(ResourceRegistry);
  private envResource$: Observable<EnvironmentResource> = this.resourceRegistry.getEnvironmentResource();

  getAspectMetadataByNameId(nameId: string): Observable<AspectMetadata> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/name/${nameId}/metadata`;
        return this.http.get<AspectMetadataDTO>(url);
      }),
      map((metadataDTO: AspectMetadataDTO) => AspectMetadataDTO.toAspectMetadata(metadataDTO))
    );
  }

  getAspectData(hashKey: string, filters: AspectFilters, clientId: string): Observable<AspectDatapoint[]> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/data`;
        const httpParams = getHttpParams({ clientSubdomain: clientId, ...filters });
        return this.http.get<AspectDatapointDTO[]>(url, { params: httpParams });
      }),
      map((aspectDataDTO: AspectDatapointDTO[]) => aspectDataDTO.map(dp => AspectDatapointDTO.toAspectDatapoint(dp)))
    );
  }

  getAspectTabularData(hashKey: string, filters: AspectFilters, clientId: string): Observable<AspectTabularData> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/data`;
        const httpParams = getHttpParams({ clientSubdomain: clientId, ...filters, format: 'tabular-echarts' });
        return this.http.get<AspectTabularData>(url, { params: httpParams });
      }),
      map((rows: AspectTabularData) => {
        verifyTabularRows(rows);
        return rows;
      })
    );
  }

  getAspectTabularRankingData(hashKey: string, clientId: string, comparisonType?: ComparisonType): Observable<AspectTabularData> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/ranking`;
        const httpParams = getHttpParams({
          clientSubdomain: clientId,
          compareWith: comparisonType ?? ComparisonType.SAME_MAJOR_BOUNDARY,
          format: 'tabular-echarts',
        });
        return this.http.get<AspectTabularData>(url, { params: httpParams });
      }),
      map((rows: AspectTabularData) => {
        verifyTabularRows(rows);
        return rows;
      })
    );
  }

  getAspectRanking(hashKey: string, clientId: string, comparisonType?: ComparisonType): Observable<AspectRanking> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/ranking`;
        const httpParams = getHttpParams({
          clientSubdomain: clientId,
          compareWith: comparisonType ?? ComparisonType.SAME_MAJOR_BOUNDARY
        });
        return this.http.get<AspectRankingDTO>(url, { params: httpParams });
      }),
      map((rankingDTO: AspectRankingDTO) => AspectRankingDTO.toAspectRanking(rankingDTO))
    );
  }

  getAspectSummary(hashKey: string, filters: AspectFilters, clientId: string, includeKpis?: boolean): Observable<AspectSummary> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/summary`;
        const httpParams = getHttpParams({ clientSubdomain: clientId, ...filters, includeKpis });
        return this.http.get<AspectSummaryDTO>(url, { params: httpParams });
      }),
      map((aspectSummaryDTO: AspectSummaryDTO) => AspectSummaryDTO.toAspectSummary(aspectSummaryDTO))
    );
  }

  getAspectView(instanceHashKey: string, clientId: string): Observable<AspectView> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${instanceHashKey}/config`;
        const httpParams = getHttpParams({ clientSubdomain: clientId });
        return this.http.get<AspectViewConfigModelDTO[]>(url, { params: httpParams });
      }),
      map((aspectViewDTO: AspectViewConfigModelDTO[]) => aspectViewDTO.map(aspectViewConfigDTO => {
        const aspectViewConfigModel = newAspectViewConfigModelFromDTO(aspectViewConfigDTO);
        if (aspectViewConfigModel[1]) {
          throw aspectViewConfigModel[1];
        }
        return aspectViewConfigModel[0];
      })),
      map(instances => ({ views: sortAspectViewsByAspectType(instances) }))
    );
  }

  getAspectContext(hashKey: string, clientId: string): Observable<AspectContext> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/context`;
        const httpParams = getHttpParams({ clientSubdomain: clientId });
        return this.http.get<AspectContextDTO>(url, { params: httpParams });
      }),
      map((contextDTO: AspectContextDTO) => AspectContextDTO.toAspectContext(contextDTO)),
    );
  }

  getDataAvailability(aspectHashKey: string, clientId: string): Observable<DataState<boolean>> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/${aspectHashKey}/data/availability`;
        const httpParams = getHttpParams({ clientSubdomain: clientId });
        return this.http.get<AspectDataAvailabilityDTO>(url, { params: httpParams });
      }),
      map((dto: AspectDataAvailabilityDTO) => AspectDataAvailabilityDTO.toIsDataAvailable(dto)),
      map((isDataAvailable: boolean) => ({
        data: isDataAvailable,
        error: '',
        status: LoadingStatus.Success,
      })),
      catchError((error: HttpErrorResponse) => {
        return of({
          data: null,
          error: error.message,
          status: LoadingStatus.Failed,
        });
      })
    );
  }

  getAspectMetadata(hashKey: string, clientId: string): Observable<AspectMetadata> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/metadata`;
        const httpParams = getHttpParams({ clientSubdomain: clientId });
        return this.http.get<AspectMetadataDTO>(url, { params: httpParams });
      }),
      map((metadataDTO: AspectMetadataDTO) => AspectMetadataDTO.toAspectMetadata(metadataDTO))
    );
  }

  getAspectFactsMetadata(hashKey: string, clientId: string): Observable<FactMetadata[]> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/metadata/facts`;
        const httpParams = getHttpParams({ clientSubdomain: clientId });
        return this.http.get<FactMetadataDTO[]>(url, { params: httpParams });
      }),
      map((factsMetadataDTO: FactMetadataDTO[]) => factsMetadataDTO.map(factMetadataDTO => FactMetadataDTO.toFactMetadata(factMetadataDTO)))
    );
  }

  getAspectDimensionsMetadata(hashKey: string, clientId: string): Observable<DimensionMetadata[]> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/metadata/dimensions`;
        const httpParams = getHttpParams({ clientSubdomain: clientId });
        return this.http.get<DimensionMetadataDTO[]>(url, { params: httpParams });
      }),
      map((dimensionsMetadataDTO: DimensionMetadataDTO[]) => dimensionsMetadataDTO.map(dto => DimensionMetadataDTO.toDimensionMetadata(dto)))
    );
  }

  getAspectSourcesMetadata(hashKey: string, clientId: string): Observable<SourceMetadata[]> {
    return this.envResource$.pipe(
      switchMap((envResource: EnvironmentResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/hash-keys/${hashKey}/metadata/sources`;
        const httpParams = getHttpParams({ clientSubdomain: clientId });
        return this.http.get<SourceMetadataDTO[]>(url, { params: httpParams });
      }),
      map((sourcesMetadataDTO: SourceMetadataDTO[]) => sourcesMetadataDTO.map(dto => SourceMetadataDTO.toSourceMetadata(dto)))
    );
  }

  getEChartsGeoJSON(locationId: string, subLocation: string): Observable<EChartsGeoJSON> {
    return this.envResource$.pipe(
      switchMap((envResource) => {
        const httpParams = getHttpParams({ IncludeSubLocations: subLocation, });
        const url = `${envResource.getBaseUrl()}/census-boundaries/geojson/boundary/${locationId}`;
        return this.http.get<EChartsGeoJSONDTO>(url, { params: httpParams });
      }),
      map((echartsGeoJSONDTO: EChartsGeoJSONDTO) => {
        return EChartsGeoJSONDTO.toEChartsGeoJSON(echartsGeoJSONDTO);
      })
    );
  }

  getAspectDataExport(hashKey: string, format: string, clientId: string): Observable<Blob> {
    return this.envResource$.pipe(
      switchMap((envResource) => {
        const url = `${envResource.getBaseUrl()}/aspect-instances/${hashKey}/data-export`;
        const httpParams = getHttpParams({ format, clientSubdomain: clientId });
        return this.http.get(url, { params: httpParams, responseType: 'blob' });
      })
    );
  }
}
